import React, { useDeferredValue, useState } from "react";
import { useHistory } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { FaPlus } from "react-icons/fa";
import adminApi from "services/admin-api";
import { DataTableSkeleton } from "components/admin";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import { DataTable } from "components/molecules/table/table";
import { AthleteForm } from "components/molecules/forms/athlete-form/athlete-form";
import RightDrawer from "components/global/RightDrawer";
import { PageHeader } from "components/admin/ui";
import { useAuth } from "context/auth-context";
import { canManageGlobalAthletes } from "utils/portal-access";
import {
  AthletesInfoRow,
  AthletesGridLayout,
  AthletesPrimaryButton,
  AthletesThumbnailImage,
  AthletesFieldInput,
  AthletesLabelText,
  AthletesMetaText,
  AthletesFieldSelect,
} from "pages/app/athletes/styles/athletes.page.inline.styles";

const emptyAthlete = {
  country: "",
  first_name: "",
  image: "",
  in_app_credits: 0,
  last_name: "",
  position: "",
  sport_id: "",
  team_ids: [],
};

const AthletesPage = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { portalAccess } = useAuth();
  const [searchValue, setSearchValue] = useState("");
  const [sportFilter, setSportFilter] = useState("all");
  const [teamFilter, setTeamFilter] = useState("all");
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const deferredSearch = useDeferredValue(searchValue);
  const { data: athletes = [], isLoading } = useQuery({
    queryKey: ["athletes"],
    queryFn: () => adminApi.athletes.list(),
  });
  const createMutation = useMutation({
    mutationFn: (payload: any) =>
      adminApi.athletes.create({
        first_name: payload.first_name,
        image: payload.image || undefined,
        in_app_credits: Number(payload.in_app_credits || 0),
        last_name: payload.last_name,
        player_profile_attributes: payload.country
          ? {
              country: payload.country,
            }
          : undefined,
        position: payload.position,
        sport_id: payload.sport_id || undefined,
        team_ids: payload.team_ids || [],
      }),
    onSuccess: () => {
      toast.success("Athlete created.");
      queryClient.invalidateQueries({
        queryKey: ["athletes"],
      });
      setDrawerOpen(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Unable to create this athlete.");
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.athletes.remove(id),
    onSuccess: () => {
      toast.success("Athlete removed.");
      queryClient.invalidateQueries({
        queryKey: ["athletes"],
      });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Unable to remove this athlete.");
    },
  });
  const sports = Array.from(
    new Set(athletes.flatMap((athlete: any) => athlete.sport_names || []).filter(Boolean)),
  ) as string[];
  const teams = Array.from(
    new Set(
      athletes
        .map((athlete: any) => athlete.primary_team?.name || athlete.team_names?.[0])
        .filter(Boolean),
    ),
  ) as string[];
  const filteredAthletes = athletes.filter((athlete: any) => {
    const matchesSearch = [
      athlete.full_name,
      athlete.position,
      athlete.country,
      athlete.primary_team?.name,
      ...(athlete.sport_names || []),
    ]
      .join(" ")
      .toLowerCase()
      .includes(deferredSearch.trim().toLowerCase());
    const primarySport = athlete.sport_names?.[0] || "Unknown";
    const primaryTeam = athlete.primary_team?.name || athlete.team_names?.[0] || "Unknown";
    const matchesSport = sportFilter === "all" || primarySport === sportFilter;
    const matchesTeam = teamFilter === "all" || primaryTeam === teamFilter;
    return matchesSearch && matchesSport && matchesTeam;
  });
  const canManageAthletes = canManageGlobalAthletes(portalAccess);
  return (
    <AppLayout route="/athletes">
      <PageHeader
        title="Athletes"
        description={
          canManageAthletes
            ? "Browse athlete profiles, followership, country coverage, and team placements, with admin-level removal for records that need cleanup."
            : "Browse athlete profiles, followership, country coverage, and team placements across the updated Athspire API."
        }
        actions={
          canManageAthletes ? (
            <AthletesPrimaryButton
              onClick={() => setDrawerOpen(true)}
              type="button"
            >
              <FaPlus size={12} />
              Add athlete
            </AthletesPrimaryButton>
          ) : null
        }
      />

      <DataTable
        canEdit={false}
        canRemove={canManageAthletes}
        columns={[
          {
            label: "Athlete",
            accessor: (row: any) => (
              <AthletesInfoRow>
                <AthletesThumbnailImage
                  alt={row.full_name || "Athlete"}
                  fallbackLabel={row.full_name}
                  src={row.avatar_url || row.image}
                  variant="athlete"
                />
                <div>
                  <AthletesLabelText>{row.full_name}</AthletesLabelText>
                  <AthletesMetaText>{row.position || "Position pending"}</AthletesMetaText>
                </div>
              </AthletesInfoRow>
            ),
          },
          {
            label: "Sport",
            accessor: (row: any) => row.sport_names?.[0] || "Unknown",
          },
          {
            label: "Primary Team",
            accessor: (row: any) => row.primary_team?.name || "Unassigned",
          },
          {
            label: "Country",
            accessor: (row: any) => row.country || "Unknown",
          },
          {
            label: "Followers",
            accessor: (row: any) => row.follower_count ?? 0,
          },
        ]}
        data={filteredAthletes}
        emptyDescription="Athlete records will appear here as soon as players are available through the API."
        emptyTitle="No athletes match these filters"
        isLoading={isLoading}
        loadingState={<DataTableSkeleton columns={5} rows={5} showThumbnail />}
        onRemove={
          canManageAthletes
            ? (id) => {
                if (window.confirm("Delete this athlete? This action cannot be undone.")) {
                  deleteMutation.mutate(String(id));
                }
              }
            : undefined
        }
        onView={(id) => history.push(`/athletes/${id}`)}
        toolbar={
          <AthletesGridLayout>
            <AthletesFieldInput
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Search athletes by name, position, sport, team, or country"
              value={searchValue}
            />
            <AthletesFieldSelect
              onChange={(event) => setSportFilter(event.target.value)}
              value={sportFilter}
            >
              <option value="all">All sports</option>
              {sports.map((sport) => (
                <option key={sport} value={sport}>
                  {sport}
                </option>
              ))}
            </AthletesFieldSelect>
            <AthletesFieldSelect
              onChange={(event) => setTeamFilter(event.target.value)}
              value={teamFilter}
            >
              <option value="all">All teams</option>
              {teams.map((team) => (
                <option key={team} value={team}>
                  {team}
                </option>
              ))}
            </AthletesFieldSelect>
          </AthletesGridLayout>
        }
      />

      {canManageAthletes ? (
        <RightDrawer
          isOpen={isDrawerOpen}
          onClose={() => {
            if (!createMutation.isPending) {
              setDrawerOpen(false);
            }
          }}
          title="Create athlete"
        >
          <AthleteForm
            initialValues={emptyAthlete}
            onCancel={() => setDrawerOpen(false)}
            onSubmit={(values) => createMutation.mutate(values)}
          />
        </RightDrawer>
      ) : null}
    </AppLayout>
  );
};
export default AthletesPage;
